import { createServerFn } from '@tanstack/react-start';
import { z } from 'zod';
import { requireStaff } from '@generated/server/staff.server';
import { db } from '@vibe/db/server';
import { classifieds, type Classified } from '@generated/models';
import { classifiedsFreeNow } from '@generated/components/classifiedPromo';
import { notifyNewClassified } from '@generated/server/notifyClassified.server';

export const CLASSIFIED_PRICE = 5;
const chars = (t: string) => t.replace(/\s/g, '').length;

const schema = z.object({
  id: z.string().optional(),
  name: z.string().trim().min(1).max(120),
  email: z.string().trim().min(3).max(200),
  phone: z.string().trim().max(40),
  category: z.string().trim().min(1).max(60),
  adText: z.string().trim().min(1).max(1200).refine((t) => chars(t) <= 250, 'Ads are limited to 250 characters, not counting spaces'),
  publication: z.enum(['Red Hook Star-Revue', 'The Village Star-Revue', 'Both']),
  issueMonth: z.string().trim().min(3).max(40),
  paymentMethod: z.string().trim().max(40),
});

export const addClassified = createServerFn({ method: 'POST' })
  .validator((d: unknown) => schema.parse(d))
  .handler(async ({ data }) => {
    const amount = classifiedsFreeNow() ? 0 : data.publication === 'Both' ? CLASSIFIED_PRICE * 2 : CLASSIFIED_PRICE;
    const { id, ...rest } = data;
    const fields = amount === 0 ? { ...rest, paymentMethod: 'Free (reader thank-you offer)' } : rest;
    if (id) {
      await db.collection(classifieds).doc(id).update({ ...fields, amount });
      return { id, amount };
    }
    // Duplicate guard: same email + same ad text + same issue → return the existing order instead of a second copy.
    const norm = (t: string) => t.toLowerCase().replace(/\s+/g, ' ').trim();
    try {
      const mine = await db.collection(classifieds).where('email', '==', data.email).limit(100).get();
      const dup = mine.find((d) => d.data.issueMonth === data.issueMonth && norm(d.data.adText) === norm(data.adText) && d.data.status !== 'Cancelled');
      if (dup) return { id: dup.id, amount: dup.data.amount ?? amount, duplicate: true as const };
    } catch (e) { console.error('[classified] duplicate check failed', e); }
    const doc = await db.collection(classifieds).add({ ...fields, amount, status: 'New', createdAt: new Date().toISOString() });
    try { await notifyNewClassified({ id: doc.id, ...fields, amount }); } catch (e) { console.error('[classified] notify failed', e); }
    return { id: doc.id, amount };
  });

export const getClassifiedReport = createServerFn({ method: 'POST' })
  .validator(z.object({ pass: z.string().optional(), issueMonth: z.string(), publication: z.string() }))
  .handler(async ({ data }) => {
    await requireStaff(data.pass);
    // Print report: needs every ad for the issue, so page through all of them.
    const all: { id: string; data: Classified }[] = [];
    let cursor: string | undefined;
    do {
      const q = db.collection(classifieds).where('issueMonth', '==', data.issueMonth).limit(100);
      const page = await (cursor ? q.startAfter(cursor) : q).getPage();
      all.push(...page.docs.map((d) => ({ id: d.id, data: d.data })));
      cursor = page.cursor;
    } while (cursor);
    const ads = all.filter((a) => data.publication === 'All' || a.data.publication === data.publication || a.data.publication === 'Both');
    return { ads };
  });
